/**
 * Created by Jie Gao on 2018-11-09.
 */
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {ScreenEvent} from "@nolimitcity/slot-game/bin/core/screen/event/ScreenEvent";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {WinPresentationController} from "@nolimitcity/slot-game/bin/core/winpresentation/WinPresentationController";
import {TimelineLite} from "gsap";
import {DragonTribeGameModuleConfig} from "../../DragonTribeGameModuleConfig";
import {DTReelController} from "../../reel/DTReelController";
import {DragonTribeParsedGameData} from "../../server/data/DragonTribeParsedGameData";
import {DragonTribeParsedInitData} from "../../server/data/DragonTribeParsedInitData";
import {FreespinFeatureEvent} from "./events/FreespinFeatureEvent";
import {FSWildMeterView} from "./FSWildMeterView";

export class FSWildMeter extends WinPresentationController {
    private readonly WILD_SYMBOLS:string[] = ["M6", "M5", "M4", "M3", "M2"];
    private readonly METER_CONFIG:any = {
        symbols : ["M6", "M5", "M4", "M3", "M2"],
        values : [0, 0, 0, 0, 0]
    };
    private _timeline:TimelineLite | null;
    private _view:FSWildMeterView;
    protected _singleWin:number = 0;
    protected _symbolValues:number[];
    protected _wildSymbols:string[] = [];
    private _avalancheCountThisRoundAfter:number = 0;
    private _avalancheCountThisRoundBefore:number = 0;
    private _hasParsedInitData:boolean = false;
    private _mode:string;
    private _isRestoreState:boolean = false;

    constructor(indexes:[number, number]) {
        super(indexes, {
            name : "FSWildMeter",
            skippable : false
        });
        this._view = new FSWildMeterView(this.METER_CONFIG);
        this._symbolValues = ArrayHelper.initArrayWithValues(this.METER_CONFIG.symbols.length, (i:number) => {
            return (i === 0 ? 0 : -1);
        });
    }

    protected addFeatureEventHandlers():void {
        EventHandler.addEventListener(this, FreespinFeatureEvent.SHOW_METER, (event:GameEvent) => this.show(event.params[0]));
        EventHandler.addEventListener(this, FreespinFeatureEvent.HIDE_METER, (event:GameEvent) => this._view.hide());
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event:GameEvent) => this.onInitGameDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ScreenEvent.GAME_READY, (event:GameEvent) => this.onRestore());
        EventHandler.addEventListener(this, SpinEvent.STOPPED, (event:GameEvent) => this.spinStopped());
    }

    private show(isRestoreFreespin:boolean):void {
        this._view.show(this._symbolValues, isRestoreFreespin);
        if(isRestoreFreespin) {
            for(let reelId:number = 0; reelId < 6; reelId++) {
                const reelController:DTReelController = <DTReelController>Reels.getReel(reelId);
                reelController.onSetWildSymbol(this._wildSymbols, false);
            }
        }
    }

    protected hasWin():boolean {
        return this._mode.indexOf("FREESPIN") > -1 && this._singleWin > 0 && this._avalancheCountThisRoundBefore < 16;
    }

    private onInitGameDataParsed(data:DragonTribeParsedInitData):void {
        this._symbolValues = [];
        this._wildSymbols = [];
        this._hasParsedInitData = true;
        this._isRestoreState = data.isRestoreState && (data.mode.indexOf("FREESPIN") > -1);
        this._avalancheCountThisRoundAfter = data.avalancheCountThisRoundAfter;
        if(data.isRestoreState) {
            this._symbolValues = ArrayHelper.initArrayWithValues(6, (i:number) => {return this._avalancheCountThisRoundAfter - i * 3;});
            for(let i:number = 0; i < Math.floor(data.avalancheCountThisRoundAfter / 3); i++) {
                this._wildSymbols.push(this.WILD_SYMBOLS[i]);
            }
        }
    }

    protected parseFeatureGameData(data:DragonTribeParsedGameData):void {
        this._singleWin = data.singleWin;
        this._mode = data.mode;
        this._avalancheCountThisRoundBefore = data.avalancheCountThisRoundBefore;
        this._avalancheCountThisRoundAfter = data.avalancheCountThisRoundAfter;
        this._symbolValues = [];
        this._wildSymbols = [];
        if(data.mode.indexOf("FREESPIN") > -1) {
            for(let i:number = 0; i < Math.floor(data.avalancheCountThisRoundAfter / 3); i++) {
                this._wildSymbols.push(this.WILD_SYMBOLS[i]);
            }
            this._symbolValues = ArrayHelper.initArrayWithValues(6, (i:number) => {return this._avalancheCountThisRoundAfter - i * 3;});
        }
    }

    protected onRestore():void {
        if(this._isRestoreState) {
            this.update(new TimelineLite(), true);
        }
    }

    private spinStopped():void {
        if(this._mode.indexOf("FREESPIN") > -1 && this._gameData!.nextMode.indexOf("FREESPIN") < 0) {
            this._view.hideGemBlink();
        }
    }

    protected startWinPresentation():void {
        const wildSyms:string[] = [];
        const progress:number = this._avalancheCountThisRoundAfter % 3;
        const level:number = this._avalancheCountThisRoundAfter / 3;

        this._timeline = new TimelineLite();
        this._view.updateGems(this._timeline, this._symbolValues);
        if(progress === 0 && level > 0 && level < 6) {
            wildSyms.push(this.METER_CONFIG.symbols[level - 1]);
            this._view.updateSymbolAnimation(this._timeline, level - 1, true);
            for(let reelId:number = 0; reelId < 6; reelId++) {
                const reelController:DTReelController = <DTReelController>Reels.getReel(reelId);
                this._timeline.add(reelController.onSetWildSymbol(wildSyms, true), "+=0");
            }
        }
        this._timeline.add(() => {this.finish();});
    }

    protected stopWinPresentation(isAborted:boolean):void {
        if(isAborted && this._timeline && this._timeline.isActive()) {
            this._timeline.pause();
            this._timeline.kill();
        }
        this._timeline = null;
        DragonTribeGameModuleConfig.background.hideRedOverlay();
    }

    public update(tl:TimelineLite, init:boolean):void {
        const progress:number = this._avalancheCountThisRoundAfter % 3;
        const level:number = this._avalancheCountThisRoundAfter / 3;
        this._view.updateGems(new TimelineLite(), this._symbolValues, init);
        if(init) {
            const activeSymbols:number = Math.min(Math.floor(level), this._symbolValues.length - 1);
            for(let i:number = 0; i < activeSymbols; i++) {
                this._view.updateSymbolAnimation(tl, i, false);
            }
        } else if(progress === 0 && level > 0 && level < 6) {
            this._view.updateSymbolAnimation(tl, level - 1, true);
        }
    }

    public reset():void {
        if(this._isRestoreState) {
            this._isRestoreState = false;
            return;
        }
        this._avalancheCountThisRoundAfter = 0;
        this._symbolValues = ArrayHelper.initArrayWithValues(this.METER_CONFIG.symbols.length, (i:number) => {
            return (i === 0 ? 0 : -1);
        });
    }
}